import React from "react";
import {
  Controller,
  FieldPath,
  get,
  useFieldArray,
  useFormContext,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Button, Checkbox, Input, Select, TextArea } from "component-library";
import { PlusIcon, TrashIcon } from "@heroicons/react/16/solid";
import { flowIds, LocalizedText, PraxisConfig } from "@shared/praxis-config";
import { config as appConfig } from "../../../config/config";
import { FieldHint, FormSection } from "../components/form-fields";
import { MarkdownEditor } from "./markdown-editor";
import {
  ConfigFormValues,
  emptyInfoPage,
  textKeys,
  themeKeys,
} from "./config-form-values";

export type PraxisConfigFormShape = {
  config: ConfigFormValues;
  favicon: FileList | null;
};

type Path = FieldPath<PraxisConfigFormShape>;

const HEX_COLOR = /^#[0-9a-f]{6}$/i;
const URL_PATTERN = /^https?:\/\/\S+$/;
const INFO_PAGE_ID = /^[a-z0-9-]+$/;

const useFieldError = () => {
  const {
    formState: { errors },
  } = useFormContext<PraxisConfigFormShape>();
  return (name: Path): string | undefined => get(errors, name)?.message;
};

type LocalizedTextFieldsProps = {
  name: Path;
  label: string;
  required?: boolean;
  /** Markdown with input and preview tabs */
  multiline?: boolean;
  /** Shown as placeholder and in the preview while no own text is set */
  defaultText?: LocalizedText;
};

/** A German and an optional English text, the English one falls back to German */
const LocalizedTextFields: React.FC<LocalizedTextFieldsProps> = ({
  name,
  label,
  required,
  multiline,
  defaultText,
}) => {
  const { t } = useTranslation();
  const { register, getValues } = useFormContext<PraxisConfigFormShape>();
  const fieldError = useFieldError();
  const dePath = `${name}.de` as Path;
  const enPath = `${name}.en` as Path;

  const deRules = {
    validate: (value: unknown) => {
      const de = String(value ?? "").trim();
      if (required && !de) return t("admin.form.required");
      if (!de && String(getValues(enPath) ?? "").trim())
        return t("admin.form.germanRequiredForEnglish");
      return true;
    },
  };

  const deProps = {
    id: dePath,
    label: `${label} (${t("admin.form.german")})`,
    placeholder: defaultText?.de,
    error: fieldError(dePath),
    ...register(dePath, deRules),
  };
  const enProps = {
    id: enPath,
    label: `${label} (${t("admin.form.english")})`,
    placeholder: t("admin.form.englishPlaceholder"),
    ...register(enPath),
  };

  if (!multiline) {
    return (
      <div className="grid gap-3 md:grid-cols-2">
        <Input {...deProps} />
        <Input {...enProps} />
      </div>
    );
  }

  const markdownClassName = { className: "font-mono text-sm" };

  return (
    <MarkdownEditor
      name={name}
      labels={{ de: deProps.label, en: enProps.label }}
      fallback={defaultText}
      hasError={!!deProps.error}
    >
      <div className="grid gap-3 md:grid-cols-2">
        <TextArea rows={6} InputProps={markdownClassName} {...deProps} />
        <TextArea rows={6} InputProps={markdownClassName} {...enProps} />
      </div>
    </MarkdownEditor>
  );
};

const LegalPageFields: React.FC<{
  name: "config.imprint" | "config.privacy";
  title: string;
  withTemplate?: boolean;
}> = ({ name, title, withTemplate }) => {
  const { t } = useTranslation();
  const { control, register, watch } = useFormContext<PraxisConfigFormShape>();
  const fieldError = useFieldError();
  const type = watch(`${name}.type`);

  const typeOptions = [
    ...(withTemplate
      ? [{ value: "template", label: t("admin.config.legal.types.template") }]
      : []),
    { value: "redirect", label: t("admin.config.legal.types.redirect") },
    { value: "markdown", label: t("admin.config.legal.types.markdown") },
  ];

  return (
    <div className="flex flex-col gap-3">
      <span className="ml-1 text-base font-semibold">{title}</span>
      <Controller
        control={control}
        name={`${name}.type`}
        render={({ field }) => (
          <Select
            options={typeOptions}
            value={field.value}
            onChange={(value) => value && field.onChange(value)}
          />
        )}
      />

      {type === "redirect" && (
        <Input
          id={`${name}.url`}
          label={t("admin.config.legal.url")}
          placeholder="https://www.praxis.de/impressum"
          error={fieldError(`${name}.url`)}
          {...register(`${name}.url`, {
            validate: (value) =>
              URL_PATTERN.test(String(value).trim()) ||
              t("admin.form.invalidUrl"),
          })}
        />
      )}

      {type === "markdown" && (
        <LocalizedTextFields
          name={`${name}.text`}
          label={t("admin.config.legal.text")}
          multiline
          required
        />
      )}

      {type === "template" && (
        <>
          <FieldHint>{t("admin.config.legal.templateHint")}</FieldHint>
          <div className="grid gap-3 md:grid-cols-2">
            {(["name", "address", "email", "phone"] as const).map((key) => (
              <Input
                key={key}
                id={`config.privacy.responsible.${key}`}
                label={t(`admin.config.legal.responsible.${key}`)}
                type={key === "email" ? "email" : "text"}
                error={fieldError(`config.privacy.responsible.${key}`)}
                {...register(`config.privacy.responsible.${key}`, {
                  validate: (value) =>
                    String(value).trim() !== "" || t("admin.form.required"),
                })}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const InfoPagesFields: React.FC = () => {
  const { t } = useTranslation();
  const { control, register } = useFormContext<PraxisConfigFormShape>();
  const fieldError = useFieldError();
  const { fields, append, remove } = useFieldArray({
    control,
    name: "config.infoPages",
  });

  return (
    <div className="flex flex-col gap-4">
      {fields.map((field, index) => (
        <div
          key={field.id}
          className="flex flex-col gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3 md:p-4"
        >
          <div className="flex items-center justify-between gap-3">
            <span className="font-semibold text-gray-800">
              {t("admin.config.infoPages.page", { number: index + 1 })}
            </span>
            <Button type="button" variant="text" onClick={() => remove(index)}>
              <TrashIcon className="h-4 w-4" />
              {t("admin.config.infoPages.remove")}
            </Button>
          </div>
          <Input
            id={`config.infoPages.${index}.id`}
            label={t("admin.config.infoPages.id")}
            placeholder="gesundheitsberatung"
            error={fieldError(`config.infoPages.${index}.id`)}
            {...register(`config.infoPages.${index}.id`, {
              validate: (value, values) => {
                const id = String(value).trim();
                if (!INFO_PAGE_ID.test(id))
                  return t("admin.config.infoPages.invalidId");
                const ids = values.config.infoPages.map((page) =>
                  page.id.trim(),
                );
                return (
                  ids.indexOf(id) === index ||
                  t("admin.config.infoPages.duplicateId")
                );
              },
            })}
          />
          <LocalizedTextFields
            name={`config.infoPages.${index}.title`}
            label={t("admin.config.infoPages.title")}
            required
          />
          <LocalizedTextFields
            name={`config.infoPages.${index}.text`}
            label={t("admin.config.infoPages.text")}
            multiline
            required
          />
        </div>
      ))}
      <Button
        type="button"
        className="self-start"
        onClick={() => append(emptyInfoPage())}
      >
        <PlusIcon className="h-4 w-4" />
        {t("admin.config.infoPages.add")}
      </Button>
    </div>
  );
};

const ThemeFields: React.FC = () => {
  const { t } = useTranslation();
  const { control, register, watch, setValue } =
    useFormContext<PraxisConfigFormShape>();
  const fieldError = useFieldError();
  const useCustomTheme = watch("config.useCustomTheme");

  return (
    <div className="flex flex-col gap-4">
      <Controller
        control={control}
        name="config.useCustomTheme"
        render={({ field }) => (
          <Checkbox value={field.value} onChange={field.onChange}>
            <span className="font-normal">
              {t("admin.config.theme.useCustomTheme")}
            </span>
          </Checkbox>
        )}
      />
      {useCustomTheme && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {themeKeys.map((key) => {
            const name = `config.theme.${key}` as const;
            const value = watch(name);

            return (
              <div key={key} className="flex items-end gap-2">
                <input
                  type="color"
                  aria-label={t(`admin.config.theme.colors.${key}`)}
                  value={HEX_COLOR.test(value) ? value : "#000000"}
                  onChange={(event) =>
                    setValue(name, event.target.value, {
                      shouldDirty: true,
                      shouldValidate: true,
                    })
                  }
                  className="mb-0.5 h-11 w-11 shrink-0 cursor-pointer rounded-lg border border-gray-500 bg-white p-1"
                />
                <Input
                  id={name}
                  className="flex-1"
                  label={t(`admin.config.theme.colors.${key}`)}
                  error={fieldError(name)}
                  {...register(name, {
                    validate: (color) =>
                      HEX_COLOR.test(String(color)) ||
                      t("admin.config.theme.invalidColor"),
                  })}
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const FaviconField: React.FC<{ subdomain?: string; hasFavicon?: boolean }> = ({
  subdomain,
  hasFavicon,
}) => {
  const { t } = useTranslation();
  const { register } = useFormContext<PraxisConfigFormShape>();
  const fieldError = useFieldError();

  return (
    <div className="flex flex-col gap-2">
      {subdomain && hasFavicon && (
        <div className="flex items-center gap-3 text-sm text-gray-600">
          <img
            src={`${appConfig.BACKEND_URL}/praxis/${encodeURIComponent(subdomain)}/favicon`}
            alt={t("admin.config.favicon.current")}
            className="h-8 w-8 rounded border border-gray-200 bg-white"
          />
          {t("admin.config.favicon.current")}
        </div>
      )}
      <input
        type="file"
        accept=".ico,.png,image/png,image/x-icon,image/vnd.microsoft.icon"
        className="text-sm text-gray-700 file:mr-3 file:cursor-pointer file:rounded-lg file:border file:border-gray-500 file:bg-white file:px-4 file:py-1.5 file:text-primary hover:file:bg-indigo-50"
        {...register("favicon", {
          validate: (files) => {
            const file = files instanceof FileList ? files[0] : undefined;
            return (
              !file ||
              file.size <= 1024 * 1024 ||
              t("admin.config.favicon.tooLarge")
            );
          },
        })}
      />
      {fieldError("favicon") && (
        <p className="ml-1 text-sm text-error">{fieldError("favicon")}</p>
      )}
      <FieldHint>
        {hasFavicon
          ? t("admin.config.favicon.hintReplace")
          : t("admin.config.favicon.hint")}
      </FieldHint>
    </div>
  );
};

type PraxisConfigFieldsProps = {
  defaults: PraxisConfig;
  subdomain?: string;
  hasFavicon?: boolean;
};

/**
 * The config of a praxis, used inside a FormProvider of PraxisConfigFormShape
 */
export const PraxisConfigFields: React.FC<PraxisConfigFieldsProps> = ({
  defaults,
  subdomain,
  hasFavicon,
}) => {
  const { t } = useTranslation();
  const { control } = useFormContext<PraxisConfigFormShape>();

  return (
    <>
      <FormSection title={t("admin.config.questionnaire.title")}>
        <div className="flex flex-col gap-1">
          <span className="ml-1 text-base font-semibold">
            {t("admin.config.questionnaire.flow")}
          </span>
          <Controller
            control={control}
            name="config.flow"
            render={({ field }) => (
              <Select
                options={flowIds.map((flow) => ({
                  value: flow,
                  label: t(`admin.config.questionnaire.flows.${flow}`),
                }))}
                value={field.value}
                onChange={(value) => value && field.onChange(value)}
              />
            )}
          />
        </div>
        <Controller
          control={control}
          name="config.contactForm"
          render={({ field }) => (
            <Checkbox value={field.value} onChange={field.onChange}>
              <span className="font-normal">
                {t("admin.config.questionnaire.contactForm")}
              </span>
            </Checkbox>
          )}
        />
      </FormSection>

      <FormSection
        title={t("admin.config.legal.title")}
        description={t("admin.config.legal.description")}
      >
        <LegalPageFields
          name="config.imprint"
          title={t("admin.config.legal.imprint")}
        />
        <LegalPageFields
          name="config.privacy"
          title={t("admin.config.legal.privacy")}
          withTemplate
        />
      </FormSection>

      <FormSection
        title={t("admin.config.texts.title")}
        description={t("admin.config.texts.description")}
      >
        {textKeys.map((key) => (
          <LocalizedTextFields
            key={key}
            name={`config.texts.${key}`}
            label={t(`admin.config.texts.${key}`)}
            defaultText={defaults.texts[key]}
            multiline
          />
        ))}
      </FormSection>

      <FormSection
        title={t("admin.config.infoPages.sectionTitle")}
        description={t("admin.config.infoPages.description")}
      >
        <InfoPagesFields />
      </FormSection>

      <FormSection
        title={t("admin.config.theme.title")}
        description={t("admin.config.theme.description")}
      >
        <ThemeFields />
      </FormSection>

      <FormSection title={t("admin.config.favicon.title")}>
        <FaviconField subdomain={subdomain} hasFavicon={hasFavicon} />
      </FormSection>
    </>
  );
};
